import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Popover } from 'antd';
import { FolderAddOutlined, UserOutlined } from '@ant-design/icons';
import { useMe } from '../hooks/useMe';
import { useIsMobile } from '../hooks/useIsMobile';
import SyncBadge from './SyncBadge';

export interface TradingPageHeaderPortfolio {
    id: string;
    portfolioName?: string;
    portfolioId?: string;
}

export interface TradingPageHeaderProps {
    /** Breadcrumb leaf, e.g. "Grid Trading" or "Pair Trading". */
    pageName: string;
    portfolios: TradingPageHeaderPortfolio[];
    selectedPortfolioId: string;
    onChangePortfolio: (id: string) => void;
    loadingPortfolios?: boolean;
    /** Optional content rendered just before the portfolio/user pill (e.g. a PnL chip). */
    rightExtra?: React.ReactNode;
}

/**
 * Shared compact header used by the Grid and Pair trading pages so the
 * portfolio selector + name area stay identical across both. Renders a
 * breadcrumb (Home / {pageName}) on the left and a pill with the portfolio
 * Select + user-account popover on the right.
 */
const TradingPageHeader: React.FC<TradingPageHeaderProps> = ({
    pageName,
    portfolios,
    selectedPortfolioId,
    onChangePortfolio,
    loadingPortfolios,
    rightExtra,
}) => {
    const navigate = useNavigate();
    const { user: meUser, loading: meLoading } = useMe();
    const isMobile = useIsMobile();

    // Compact shell: the breadcrumb's Home link and the user pill both moved into the
    // tab bar / More sheet, so the header keeps only what is page-specific — the page
    // name, the portfolio being operated on, and whatever chip the page passes in.
    if (isMobile) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                borderBottom: '1px solid #2B3139',
                flexShrink: 0,
            }}>
                <span style={{ color: '#EAECEF', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>
                    {pageName}
                </span>
                {rightExtra}
                <Select
                    value={selectedPortfolioId}
                    onChange={(value) => {
                        if (value === '__new__') {
                            navigate('/register-portfolio');
                        } else {
                            onChangePortfolio(value);
                        }
                    }}
                    style={{ flex: 1, minWidth: 0, marginLeft: 'auto', maxWidth: '58%' }}
                    size="small"
                    loading={loadingPortfolios}
                    disabled={loadingPortfolios}
                    placeholder={loadingPortfolios ? 'Loading...' : 'Portfolio'}
                >
                    {portfolios.map((portfolio) => (
                        <Select.Option key={portfolio.id} value={portfolio.id}>
                            {portfolio.portfolioName || portfolio.portfolioId}
                        </Select.Option>
                    ))}
                    <Select.Option key="__new__" value="__new__">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0ECB81' }}>
                            <FolderAddOutlined /> New Portfolio
                        </div>
                    </Select.Option>
                </Select>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 8px 8px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <span style={{ color: '#EAECEF', fontWeight: 500 }}>{pageName}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SyncBadge portfolioId={selectedPortfolioId} />
                {rightExtra}
                <div data-tour="portfolio-select" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0',
                    background: '#1E2329',
                    borderRadius: '16px',
                    border: '1px solid #2B3139',
                    padding: '2px',
                    height: '26px',
                }}>
                    <Select
                        value={selectedPortfolioId}
                        onChange={(value) => {
                            if (value === '__new__') {
                                navigate('/register-portfolio');
                            } else {
                                onChangePortfolio(value);
                            }
                        }}
                        style={{ width: '170px', height: '22px' }}
                        size="small"
                        loading={loadingPortfolios}
                        disabled={loadingPortfolios}
                        placeholder={loadingPortfolios ? 'Loading...' : 'Select portfolio'}
                        variant="borderless"
                    >
                        {portfolios.map((portfolio) => (
                            <Select.Option key={portfolio.id} value={portfolio.id}>
                                {portfolio.portfolioName || portfolio.portfolioId}
                            </Select.Option>
                        ))}
                        <Select.Option key="__new__" value="__new__">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0ECB81' }}>
                                <FolderAddOutlined /> New Portfolio
                            </div>
                        </Select.Option>
                    </Select>
                    <div style={{ width: '1px', height: '14px', background: '#2B3139' }} />
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        styles={{ body: { background: '#1A1D23', border: '1px solid #2B3139', padding: 0 } }}
                        content={
                            meLoading ? (
                                <div style={{ padding: '16px', color: '#848E9C' }}>Loading...</div>
                            ) : meUser ? (
                                <div style={{ padding: '16px', minWidth: '220px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '16px',
                                        paddingBottom: '12px',
                                        borderBottom: '1px solid #2B3139',
                                    }}>
                                        <div style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #0ECB81 0%, #1E90FF 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <UserOutlined style={{ fontSize: '16px', color: '#fff' }} />
                                        </div>
                                        <div>
                                            <div style={{ color: '#EAECEF', fontWeight: 600, fontSize: '14px' }}>
                                                {meUser.name || 'User'}
                                            </div>
                                            <div style={{ color: '#848E9C', fontSize: '12px' }}>
                                                {meUser.role}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div>
                                            <div style={{ color: '#848E9C', fontSize: '11px', marginBottom: '2px' }}>Email</div>
                                            <div style={{ color: '#EAECEF', fontSize: '13px' }}>{meUser.email}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#848E9C', fontSize: '11px', marginBottom: '2px' }}>Member since</div>
                                            <div style={{ color: '#EAECEF', fontSize: '13px' }}>
                                                {new Date(meUser.createdAt).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '2px 8px 2px 6px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #0ECB81 0%, #1E90FF 100%)',
                            cursor: 'pointer',
                            height: '22px',
                        }}>
                            <UserOutlined style={{ fontSize: '11px', color: '#fff' }} />
                            <span style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                            }}>
                                {meLoading ? '...' : (meUser?.name || meUser?.email || 'User')}
                            </span>
                        </div>
                    </Popover>
                </div>
            </div>
        </div>
    );
};

export default TradingPageHeader;
